import { Form, Input, Select, Card, message } from "antd";
import {  useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import TailwindButton from "components/Button";
import {
  headerStyles,
  languageOptions,
  scriptStyleOptions,
  stepsEnum,
  targetPlatformOptions,
  videoAspectRatioOptions,
  videoLengthOptions,
  visualStyleOptions,
} from "./options";
import { useVideoValidationRules } from "./validationSchema";

const StepTwo = ({ itemId, cachedData, onNext = () => {} }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const { validationRules, validateForm } = useVideoValidationRules();

  const [credits, setCredits] = useState(0)

  const debounceTimeoutRef = useRef(null);  // 👈 native debounce ref

  const handleSubmit = (values) => {
    const { isFormValid, errors } = validateForm(values);
    if (!isFormValid) {
      const firstError = Object.values(errors)[0];
      message.error(firstError);
      return;
    }
    onNext(stepsEnum.stepThree, {...values, credits_used: credits}, false);
  };

  // const generateAIScript = async () => {
  //   const payload = form.getFieldsValue()
  //   delete payload.override_script
  //   const response = await GetAIScript(payload)
  //   if (response) {
  //     setCredits(response.credits_used)
      
  //   }

  // }

  const initialValues = useMemo(() => {
    const defaultValues = cachedData?.stepTwo ?? {
      target_platform: "tiktok",
      script_style: "DiscoveryWriter",
      visual_style: "AvatarBubbleTemplate",
      language: "en",
      video_length: 15,
      aspect_ratio: "16x9",
      name: cachedData?.stepOne?.link?.title ?? "",
      target_audience: cachedData?.stepOne?.link?.ai_target_audiences?.[0] ?? "",
      override_script: cachedData?.stepOne?.link?.ai_summary ?? "",
    };
    form.setFieldsValue(defaultValues);
    return defaultValues;
  }, [cachedData]);
  

  // const debouncedOnValuesChange = (allValues) => {
  //   if (debounceTimeoutRef.current) {
  //     clearTimeout(debounceTimeoutRef.current);
  //   }
  //   debounceTimeoutRef.current = setTimeout(() => {
  //     generateAIScript()
  //   }, 500);
  // };

  const fields = useMemo(() => [
    {
      name: "name",
      label: t("Name of the video"),
      as: "Input",
      placeholder: t("Name of the video"),
      rules: validationRules.name,
    },
    {
      name: "language",
      label: t("Language of the video"),
      options: languageOptions,
      as: "Select",
      placeholder: t("Language of the video"),
      required: true,
      rules: validationRules.language,
    },
    {
      name: "target_platform",
      label: t("Target platform for the video"),
      options: targetPlatformOptions,
      as: "Select",
      placeholder: t("Target platform for the video"),
      required: true,
      rules: validationRules.target_platform,
    },
    {
      name: "target_audience",
      label: t("Target audience for the video"),
      options: cachedData?.stepOne?.link?.ai_target_audiences?.map((audience) => ({
        label: audience,
        value: audience,
      })) ?? [],
      as: "Select",
      placeholder: t("Target audience for the video"),
      required: true,
      rules: validationRules.target_audience,
    },
    {
      name: "aspect_ratio",
      label: t("Aspect ratio of the video"),
      options: videoAspectRatioOptions,
      as: "Select",
      placeholder: t("Aspect ratio of the video"),
      required: true,
      rules: validationRules.aspect_ratio,
    },
    {
      name: "video_length",
      label: t("Length of the video in seconds"),
      options: videoLengthOptions,
      as: "Select",
      placeholder: t("Length of the video in seconds"),
      required: true,
      rules: validationRules.video_length,
    },
    {
      name: "script_style",
      label: t("Script style of the video"),
      options: scriptStyleOptions,
      as: "Select",
      placeholder: t("Script style of the video"),
      required: true,
      rules: validationRules.script_style,
      extra: t("Select the tone and format for the video script (e.g., formal, casual, narrative)."),
    },
    {
      name: "visual_style",
      label: t("Visual style of the video"),
      options: visualStyleOptions,
      as: "Select",
      placeholder: t("Visual style of the video"),
      required: true,
      rules: validationRules.visual_style,
      extra: t("Choose the preferred visual aesthetic for the video (e.g., cinematic, animated, modern)."),
    },
    {
      name: "override_script",
      label: t("AI Description"),
      as: "TextArea",
      placeholder: t("Additional description or instructions for AI video generation"),
      className: "col-span-1 md:col-span-2",
      rows: 4,
      required: true,
      rules: validationRules.override_script,
      extra: t("Provide extra context or specific instructions for the AI-generated video script."),
    },
  ], [cachedData, t, validationRules]);

  return (
    <Card title={t("Video Creation Settings")} headStyle={headerStyles}>
      <Form
        form={form}
        id="stepTwoForm"
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleSubmit}
        // onValuesChange={(changedValues, allValues) => {
        //   debouncedOnValuesChange(allValues);
        // }}
        className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6"
      >
        {fields.map((field) => (
          <Form.Item key={field.name} {...field} style={{ marginBottom: 0 }}>
            {field.as === "Select" ? (
              <Select options={field.options} disabled={field.disabled || field.options.length <= 0} />
            ) : field.as === "TextArea" ? (
              <Input.TextArea
                placeholder={field.placeholder}
                disabled={field.disabled}
                rows={field.rows}
                loading={true}
              />
            ) : (
              <Input
                placeholder={field.placeholder}
                disabled={field.disabled}
              />
            )}
          </Form.Item>
        ))}

        <Form.Item>
          <TailwindButton
            text={"Save & Continue"}
            type="submit"
            formId="stepTwoForm"
          />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default StepTwo;
